import supabase from '../supabase/supabase'

/**
 * Storage Service
 * Handles file uploads/downloads with Supabase Storage
 */

/**
 * Get current user's tenant ID
 */
const getCurrentTenantId = async () => {
  const { data: { user } } = await supabase.auth.getUser()
  
  if (!user) {
    throw new Error('No authenticated user')
  }

  const { data, error } = await supabase
    .from('tenant_members')
    .select('tenant_id')
    .eq('user_id', user.id)
    .single()

  if (error) throw error
  
  return data.tenant_id
}

/**
 * Upload file to storage
 */
export const uploadFile = async (bucket, file, metadata = {}) => {
  try {
    const tenantId = await getCurrentTenantId()
    const { data: { user } } = await supabase.auth.getUser()

    // Generate unique file name
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
    const filePath = `${tenantId}/${metadata.folder || 'general'}/${fileName}`

    // Upload file
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      })

    if (error) throw error

    // Get public URL (for signed URLs later)
    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath)

    return {
      path: data.path,
      fullPath: filePath,
      publicUrl,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      uploadedBy: user.id
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

/**
 * Get signed URL for private file access
 */
export const getSignedUrl = async (bucket, filePath, expiresIn = 3600) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(filePath, expiresIn)

    if (error) throw error

    return data.signedUrl
  } catch (error) {
    console.error('Error getting signed URL:', error)
    throw error
  }
}

/**
 * Download file
 */
export const downloadFile = async (bucket, filePath) => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(filePath)

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}

/**
 * Delete file
 */
export const deleteFile = async (bucket, filePath) => {
  try {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath])

    if (error) throw error

    return true
  } catch (error) {
    console.error('Error deleting file:', error)
    throw error
  }
}

/**
 * List files in folder
 */
export const listFiles = async (bucket, folder = '') => {
  try {
    const tenantId = await getCurrentTenantId()
    const path = folder ? `${tenantId}/${folder}` : tenantId

    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path)

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error listing files:', error)
    throw error
  }
}

/**
 * Upload case document
 */
export const uploadCaseDocument = async (caseId, file, documentType, description = '') => {
  try {
    // Upload to storage
    const uploadResult = await uploadFile('case-documents', file, {
      folder: `cases/${caseId}`
    })

    // Save metadata to database
    const { data, error } = await supabase
      .from('case_documents')
      .update({
        file_path: uploadResult.fullPath,
        file_size: uploadResult.fileSize
      })
      .eq('file_path', uploadResult.fullPath) // This is a placeholder - you'd pass the document ID

    return uploadResult
  } catch (error) {
    console.error('Error uploading case document:', error)
    throw error
  }
}

/**
 * Upload staff document
 */
export const uploadStaffDocument = async (staffId, file, documentType) => {
  try {
    const uploadResult = await uploadFile('staff-documents', file, {
      folder: `staff/${staffId}`
    })

    return uploadResult
  } catch (error) {
    console.error('Error uploading staff document:', error)
    throw error
  }
}

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Get file icon based on type
 */
export const getFileIcon = (fileType) => {
  if (fileType.includes('pdf')) return 'FiFileText'
  if (fileType.includes('image')) return 'FiImage'
  if (fileType.includes('word') || fileType.includes('document')) return 'FiFileText'
  if (fileType.includes('excel') || fileType.includes('sheet')) return 'FiGrid'
  if (fileType.includes('zip') || fileType.includes('rar')) return 'FiArchive'
  return 'FiFile'
}

export default {
  uploadFile,
  getSignedUrl,
  downloadFile,
  deleteFile,
  listFiles,
  uploadCaseDocument,
  uploadStaffDocument,
  formatFileSize,
  getFileIcon
}